import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatDialogClose, MatDialogRef } from '@angular/material/dialog';
import { ButtonComponent } from '@atoms/button/button.component';
import { CheckboxComponent } from '@atoms/checkbox/checkbox.component';
import { LinkButtonComponent } from '@atoms/link-button/link-button.component';
import { environment } from '@env/environment';
import { ModalLayoutComponent } from '@organisms/modal-layout/modal-layout.component';
import { ConsentService } from '@services/consent.service';
import { PosthogService } from '@services/posthog.service';
import { POSTHOG_EVENTS } from '@services/posthog-events';

@Component({
  selector: 'iahoy-analytics-preferences-modal',
  imports: [
    ModalLayoutComponent,
    CheckboxComponent,
    ButtonComponent,
    LinkButtonComponent,
    MatDialogClose,
    ReactiveFormsModule
  ],
  templateUrl: './analytics-preferences-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './analytics-preferences-modal.component.scss'
})
export class AnalyticsPreferencesModalComponent {
  private readonly dialogRef = inject(MatDialogRef<AnalyticsPreferencesModalComponent>);
  private readonly consentService = inject(ConsentService);
  private readonly posthogService = inject(PosthogService);

  readonly analyticsControl = new FormControl(this.consentService.isAnalyticsAccepted(), { nonNullable: true });
  private readonly analyticsSelection = toSignal(this.analyticsControl.valueChanges, {
    initialValue: this.analyticsControl.getRawValue()
  });
  readonly hasSelectionChanged = computed(
    () => this.analyticsSelection() !== this.consentService.isAnalyticsAccepted()
  );
  readonly cookiePolicyUrl = new URL('/cookie-policy/', environment.landingUrl).toString();
  readonly privacyPolicyUrl = new URL('/privacy-policy/', environment.landingUrl).toString();

  close(): void {
    this.dialogRef.close(false);
  }

  save(): void {
    const previousValue = this.consentService.isAnalyticsAccepted();
    const nextValue = this.analyticsControl.getRawValue();

    if (previousValue && !nextValue) {
      void this.posthogService.capture(POSTHOG_EVENTS.analyticsConsentUpdated, {
        source: 'preferences_modal',
        analytics_enabled: false
      });
    }

    this.consentService.saveAnalyticsConsent(nextValue);

    if (nextValue) {
      void this.posthogService.capture(POSTHOG_EVENTS.analyticsConsentUpdated, {
        source: 'preferences_modal',
        analytics_enabled: true
      });
    }

    this.dialogRef.close(true);
  }
}
